import { swap } from "../core/swap";
import { getConfig, setConfig } from "../constants/config";
import { getRouteDetails } from "../helpers/getRouteDetails";
import { TokenListByAddress } from "../../common/constants/tokens";
import { getSwapTypes } from "../helpers/getSwapTypes";
import { getTokenIn } from "../helpers/getTokenIn";
import { getTokenOut } from "../helpers/getTokenOut";
import { getTotalAmountIn } from "../helpers/getTotalAmountIn";
import { getTotalAmountOut } from "../helpers/getTotalAmountOut";
import { getRoute } from "../core/getRoute";
import { checkApproval } from "../core/checkApproval";
import { giveApproval } from "../core/giveApproval";
import { generateCalldata } from "../helpers/generateCalldata";
export class Route {
  constructor(routePayload, getWalletClient, options) {
    this.amountIn = routePayload.amountIn;
    this.amountInFormatted = routePayload.amountInFormatted;
    this.includesNative = routePayload.includesNative;
    this.pathCount = routePayload.pathCount;
    this.pathPercentages = routePayload.pathPercentages;
    this.amountOut = routePayload.amountOut;
    this.amountOutFormatted = routePayload.amountOutFormatted;
    this.route = routePayload.route;
    this.rebate = routePayload.rebate;
    this.tokenIn = routePayload.tokenIn;
    this.tokenOut = routePayload.tokenOut;
    this.tokenInUsd = routePayload.tokenInUsd;
    this.tokenOutUsd = routePayload.tokenOutUsd;
    this.swapFee = routePayload.swapFee;
    this.priceImpactPercentage = routePayload.priceImpactPercentage;
    this.getWalletClient = getWalletClient;
    this.options = options;
  }

  async swap({
    route = this.route,
    includesNative = this.includesNative,
    walletClient = this.getWalletClient(),
    slippage = {},
    options = {},
  } = {}) {
    // Default values for options
    const defaultOptions = {
      skipApproval: true,
      skipCheckApproval: false,
      wait: 1,
    };

    // Default values for slippage
    const defaultSlippage = {
      slippageAmount: getConfig().slippage,
      isBps: false,
    };
    // Merge user-provided values with defaults
    const mergedOptions = { ...defaultOptions, ...options };
    const mergedSlippage = { ...defaultSlippage, ...slippage };

    return await swap({
      route,
      includesNative,
      walletClient,
      slippage: mergedSlippage,
      options: mergedOptions,
    });
  }

  /**
   * Encodes the calldata for this route's swap transaction without executing it.
   * This is useful for transaction simulation using eth_call.
   *
   * @param {Object} params - Parameters for encoding swap calldata
   * @param {string|Object} [params.from=this.getWalletClient()] - From address or wallet client
   * @param {Array} [params.route=this.route] - Route to use for swap
   * @param {boolean} [params.includesNative=this.includesNative] - Whether route includes native token
   * @param {Object} [params.slippage] - Slippage configuration
   * @param {string} [params.slippage.slippageAmount=config.slippage] - Slippage tolerance
   * @param {boolean} [params.slippage.isRaw=true] - If true, slippage is in raw units
   * @param {boolean} [params.slippage.isBps=false] - If true, slippage is in basis points
   * @param {number} [params.slippage.outTokenDecimals] - Output token decimals
   *
   * @returns {Promise<Object>} Object containing the encoded calldata and transaction value
   *
   * @example
   * // Get calldata with default options
   * const tx = await route.generateCalldata();
   *
   * @example
   * // Get calldata with custom from address
   * const tx = await route.generateCalldata({
   *   from: "0xa2a9dd657D44e46E2d1843B8784eFc3dE3Cf3A57"
   * });
   *
   * // Use with eth_call
   * const result = await publicClient.call(tx);
   */
  async generateCalldata({
    from = this.getWalletClient(),
    route = this.route,
    includesNative = this.includesNative,
    slippage = {},
    options = {},
  } = {}) {
    const defaultOptions = {
      isRaw: true,
      outTokenDecimals: undefined,
    };
    const defaultSlippage = {
      slippageAmount: getConfig().slippage,
      isBps: false,
    };
    const mergedOptions = { ...defaultOptions, ...options };
    const mergedSlippage = { ...defaultSlippage, ...slippage };

    return await generateCalldata({
      from,
      route,
      includesNative,
      slippage: mergedSlippage,
      options: mergedOptions,
    });
  }

  async refresh() {
    const routePayload = await getRoute(
      this.tokenIn,
      this.tokenOut,
      this.amountInFormatted,
      this.options
    );

    // Reuse the same getter function
    return new Route(routePayload, this.getWalletClient, this.options);
  }

  async checkApproval({
    variable = this.route,
    walletClient = this.getWalletClient(),
    includesNative = this.includesNative,
    options = {},
  } = {}) {
    const defaultOptions = { isRaw: true, chainId: getConfig().chainId };
    const mergedOptions = { ...defaultOptions, ...options };
    return await checkApproval({
      variable,
      walletClient,
      includesNative,
      options: mergedOptions,
    });
  }

  async giveApproval({
    variable = this.route,
    walletClient = this.getWalletClient(),
    amount = this.amountIn,
    options = {},
  } = {}) {
    const defaultOptions = { isRaw: true, chainId: getConfig().chainId };
    const mergedOptions = { ...defaultOptions, ...options };
    return await giveApproval({
      variable,
      walletClient,
      amount,
      options: mergedOptions,
    });
  }

  async getRouteDetails(route = this.route) {
    return await getRouteDetails(route);
  }

  // Get swap types
  getSwapTypes(route = this.route) {
    return getSwapTypes(route);
  }

  // Get token in address
  getTokenIn(route = this.route) {
    return getTokenIn(route);
  }

  // Get token out address
  getTokenOut(route = this.route) {
    return getTokenOut(route);
  }

  // Get total amount in for the route
  async getTotalAmountIn(route = this.route) {
    return await getTotalAmountIn(route);
  }

  // Get out amount of the route
  async getTotalAmountOut(route = this.route) {
    return await getTotalAmountOut(route);
  }

  // Access configuration
  getConfig() {
    return getConfig();
  }

  // Update configuration dynamically
  setConfig(options) {
    setConfig(options);
  }
}
